import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import { clearAuditHistory, getAuditList } from "actions/audit";
import {
  renderDate,
  renderStateChange,
} from "components/DataTable/renderComponents";
import { DataTable } from "empower-energy-owner-components";
import { useEffect, useMemo, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getSortOrder } from "utils/xGrid";
import debounce from "lodash.debounce";

const buildFilters = (page, pageSize, sortModel) => ({
  limit: pageSize,
  offset: page * pageSize,
  order: getSortOrder(sortModel),
});

const AuditHistory = ({ type = "all" }) => {
  const dispatch = useDispatch();

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });

  const [sortModel, setSortModel] = useState([
    { field: "created_at", sort: "desc" },
  ]);

  const [filters, setFilters] = useState(
    buildFilters(0, 10, [{ field: "created_at", sort: "desc" }]),
  );

  const [entityType, setEntityType] = useState("unit");

  useEffect(() => {
    if (type === "unit") setEntityType("unit");
    if (type === "site") setEntityType("site");
  }, [type]);

  const showRadio = type === "all";

  const loading = useSelector((state) => state.audit?.loading);

  const unitAuditList = useSelector((state) => state.audit?.unitAuditList);
  const unitAuditTotalCount = useSelector(
    (state) => state.audit?.unitAuditTotalCount,
  );

  const siteAuditList = useSelector((state) => state.audit?.siteAuditList);
  const siteAuditTotalCount = useSelector(
    (state) => state.audit?.siteAuditTotalCount,
  );

  const details = useSelector((state) => state.unit?.details);
  const siteDetails = useSelector((state) => state.site?.details);

  const isUnit = entityType === "unit";

  const fetchAuditList = useCallback(
    debounce((id, type, filters) => {
      dispatch(getAuditList(id, type, filters));
    }, 300),
    [dispatch],
  );

  useEffect(() => {
    return () => {
      dispatch(clearAuditHistory());
    };
  }, [dispatch]);

  useEffect(() => {
    const id = entityType === "unit" ? details?.id : siteDetails?.id;

    if (id) {
      fetchAuditList(id, entityType, filters);
    }
  }, [entityType, details?.id, siteDetails?.id, filters, fetchAuditList]);

  useEffect(() => {
    setFilters(() =>
      buildFilters(paginationModel.page, paginationModel.pageSize, sortModel),
    );
  }, [paginationModel, sortModel]);

  const columns = useMemo(
    () => [
      {
        field: "id",
        headerName: "Change",
        minWidth: 300,
        flex: 1,
        renderCell: (params) => renderStateChange(params),
      },
      {
        field: "note",
        headerName: "Note",
        minWidth: 300,
      },
      {
        field: "created_at",
        headerName: "Created",
        width: 200,
        renderCell: (params) => renderDate(params.value * 1000),
      },
      {
        field: "created_by",
        headerName: "Created By",
        width: 300,
      },
    ],
    [],
  );

  return (
    <Box>
      {showRadio && (
        <FormControl sx={{ mb: 2 }}>
          <RadioGroup
            row
            name="entityType"
            value={entityType}
            onChange={(e) => setEntityType(e.target.value)}
          >
            <FormControlLabel label="Unit" value="unit" control={<Radio />} />
            <FormControlLabel label="Site" value="site" control={<Radio />} />
          </RadioGroup>
        </FormControl>
      )}

      <DataTable
        columns={columns}
        rows={isUnit ? unitAuditList : siteAuditList}
        totalCount={isUnit ? unitAuditTotalCount : siteAuditTotalCount}
        loading={loading}
        paginationMode="server"
        paginationModel={paginationModel}
        setPaginationModel={setPaginationModel}
        pageSizeOptions={[5, 10, 25, 50, 100]}
        sortingMode="server"
        sortModel={sortModel}
        setSortModel={setSortModel}
      />
    </Box>
  );
};

export default AuditHistory;
